import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ForwardedRef, forwardRef } from 'react';

import clsx from 'clsx';

import styles from './Button.module.scss';

// Use a union type instead of intersection to handle the different onClick signatures
type ButtonProps = {
  variant?:
    | 'filled'
    | 'outlined'
    | 'text'
    | 'error'
    | 'error-outlined'
    | 'tonal'
    | 'error-text'
    | 'signup-flow-primary'
    | 'signup-flow-secondary';
  as?: React.ElementType;
} & (
  | (ButtonHTMLAttributes<HTMLButtonElement> & { href?: never })
  | (AnchorHTMLAttributes<HTMLAnchorElement> & { href: string })
);

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({ children, className, variant = 'filled', as, ...props }, ref) => {
    // Automatically detect if this should be a link (when href is passed from NextLink)
    const Component = as || (props.href ? 'a' : 'button');

    // Don't pass type="button" to anchor elements
    const finalProps = Component === 'a' ? props : { type: 'button' as const, ...props };

    return (
      <Component
        ref={ref as never}
        className={clsx(
          styles.button,
          {
            [styles['variant-filled']]: variant === 'filled',
            [styles['variant-outlined']]: variant === 'outlined',
            [styles['variant-text']]: variant === 'text',
            [styles['variant-error']]: variant === 'error',
            [styles['variant-error-outlined']]: variant === 'error-outlined',
            [styles['variant-tonal']]: variant === 'tonal',
            [styles['variant-error-text']]: variant === 'error-text',
            [styles['variant-signup-flow-primary']]: variant === 'signup-flow-primary',
            [styles['variant-signup-flow-secondary']]: variant === 'signup-flow-secondary',
          },
          className,
        )}
        {...(finalProps as any)}
      >
        {children}
      </Component>
    );
  },
);
Button.displayName = 'Button';
